import { Formik, FormikProps } from 'formik';
import { sumBy } from 'lodash';
import React, { useState } from 'react';
import * as yup from 'yup';
import {
    applyBulkActionToMedicationList,
    createToMedicationList,
    validateMedicationListName
} from '../../Async/MedicationListsAsync';
import { useError } from '../../Components/ErrorBoundary';
import { FullScreenModal } from '../../Components/FullScreenModal';
import { StyledNavigationPrompt } from '../../Components/Prompts/StyledNavigationPrompt';
import { displayMessage } from '../../Config/ApplicationConfig';
import { TimeUnits, useMedicationSettings } from '../../Config/MedicationConfig';
import { BulkActionRequest, Slicer } from '../../Contracts/BulkActionRequest.Request';
import { CreateMedicationList } from '../../Contracts/CreateMedicationList.Request';
import { ListBulkAction, ListSearchAction } from '../../Contracts/MedicationListModels';
import { ValidationResult } from '../../Contracts/ValidationResult.Response';
import { requiredField } from '../../Medications.en';
import { defaultAuthoringSystem, selfAuthoringSystemForeignKey } from '../../Shared/Constants';
import { debouncePromise } from '../../Shared/debouncePromise';
import { isDomainValidationError, ValidationFailure } from '../../Shared/DomainValidationFunctions';
import { Reference } from '../../Shared/SharedModels';
import { Maybe } from '../../Shared/Types';
import {
    MedicationListDefaultOverride,
    DefaultDuration,
    MedicationListDefaultOverrideStepState
} from '../MedicationListSharedSteps/MedicationListDefaultOverride';
import { MedicationListErrors } from '../MedicationListSharedSteps/MedicationListErrors';
import { MedicationListSliceStep, SliceStepState } from '../MedicationListSharedSteps/MedicationListSliceStep';
import {
    createMedicationListStepFields,
    MedicationInformationStep,
    MedicationListFormErrors_TypeMissing,
    MedicationListInformationStep
} from '../MedicationListSharedSteps/MedicationListInformationStep';
import { CreateMedicationListButtons } from './CreateMedicationListButtons';
import styles from './CreateMedicationListDialog.module.scss';
import { CreatingMedicationListPlaceholder } from './CreatingMedicationListPlaceholder';

export enum CreateMedicationListStep {
    ListInformation,
    ListSlices,
    DefaultOverride,
    CreatingMedicationList,
    Error
}

function getTitle(step: CreateMedicationListStep) {
    switch (step) {
        case CreateMedicationListStep.ListInformation:
            return 'Create a medication list';
        case CreateMedicationListStep.ListSlices:
            return 'Add medication to your list';
        case CreateMedicationListStep.DefaultOverride:
            return 'Define your default dosage instructions for your list';
        case CreateMedicationListStep.Error:
            return 'Unable to update medication list';
        case CreateMedicationListStep.CreatingMedicationList:
            return '';
    }
}

function getFormInfo(step: CreateMedicationListStep) {
    switch (step) {
        case CreateMedicationListStep.ListInformation:
            return 'Provide a meaningful name and select the type of list you want to create.';
        case CreateMedicationListStep.ListSlices:
            return 'Add groups of medications that will share common prescribing characteristics. Select at least one data set to create a valid medication list.';
        case CreateMedicationListStep.DefaultOverride:
            return 'The duration and dosage are used by default for any drug from the collection. You can customise individual drugs later. Use to help manage drug review cycles and streamline prescribing.';
        case CreateMedicationListStep.Error:
            return '';
        case CreateMedicationListStep.CreatingMedicationList:
            return '';
    }
}

function getProgress(step: CreateMedicationListStep): Maybe<number> {
    switch (step) {
        case CreateMedicationListStep.ListInformation:
            return 0;
        case CreateMedicationListStep.ListSlices:
            return 33;
        case CreateMedicationListStep.DefaultOverride:
            return 66;
        case CreateMedicationListStep.CreatingMedicationList:
            return; // Return undefined to hide progress bar.
    }
}

export type CreateMedicationListForm = MedicationInformationStep &
    SliceStepState &
    MedicationListDefaultOverrideStepState;

const debouncedValidateMedicationListName = debouncePromise<ValidationResult>(validateMedicationListName, 250);

const createMedicationListSchema = yup
    .object<CreateMedicationListForm>({
        name: yup
            .string()
            .required(requiredField)
            .test({
                name: 'Name must be unique',
                message: 'Duplicate medication name exists',
                test: async function validateName(this: yup.TestContext, value: string) {
                    if (!value) return true;
                    const result = await debouncedValidateMedicationListName(value);
                    return result.success;
                }
            }),
        isScopeOfPracticeList: yup.bool().required(requiredField),
        isFormulary: yup.bool().required(requiredField),
        isDefaultList: yup.bool().required(requiredField),
        bulkAction: yup.mixed<ListBulkAction>(),
        atcCodes: yup.array<any>().test({
            name: 'Characteristic required',
            message: 'At least one item required',
            test(this: yup.TestContext, value: Reference[]) {
                const form = this.parent as CreateMedicationListForm;

                if (value.length > 0) return true;

                return form.schedules.length > 0 || form.medicinalProducts.length > 0;
            }
        }),
        schedules: yup.array<any>().test({
            name: 'Characteristic required',
            message: 'At least one item required',
            test(this: yup.TestContext, value: Reference[]) {
                const form = this.parent as CreateMedicationListForm;

                if (value.length > 0) return true;

                return form.atcCodes.length > 0 || form.medicinalProducts.length > 0;
            }
        }),
        medicinalProducts: yup.array<any>().test({
            name: 'Characteristic required',
            message: 'At least one item required',
            test(this: yup.TestContext, value: Reference[]) {
                const form = this.parent as CreateMedicationListForm;

                if (value.length > 0) return true;

                return form.schedules.length > 0 || form.atcCodes.length > 0;
            }
        }),
        defaultDosageInstructions: yup.string().test({
            name: 'Default dosage instructions must exist if overridden is active',
            message: requiredField,
            test(this: yup.TestContext, value: string) {
                const form = this.parent as CreateMedicationListForm;
                if (form.defaultDosageInstructionsOverridden) {
                    if (!value) return false;
                }

                return true;
            }
        }),
        defaultDosageInstructionsOverridden: yup.boolean(),
        defaultDuration: yup.object<any>().test({
            name: 'Default duration must have a number if overridden is active',
            message: requiredField,
            test(this: yup.TestContext, value: DefaultDuration) {
                const form = this.parent as CreateMedicationListForm;
                if (form.defaultDurationOverridden) {
                    if (!value.number) return false;
                }

                return true;
            }
        }),
        defaultDurationOverridden: yup.boolean(),
        defaultPackAmount: yup.string().test({
            name: 'Default pack amount must have a number if overridden is active',
            message: requiredField,
            test(this: yup.TestContext, value: string) {
                const form = this.parent as CreateMedicationListForm;
                if (form.defaultPackAmountOverridden) {
                    if (!value) return false;
                }

                return true;
            }
        }),
        defaultPackAmountOverridden: yup.boolean()
    })
    .test({
        name: 'One type of list must be chosen',
        message: 'One type of list must be chosen',
        test(this: yup.TestContext, value: CreateMedicationListForm) {
            if (value.isDefaultList) return true;
            if (value.isScopeOfPracticeList) return true;
            if (value.isFormulary) return true;
            return this.createError({
                message: 'Medication list type required.',
                path: MedicationListFormErrors_TypeMissing
            });
        }
    });

const MIN_CREATE_DURATION = 2000;

export const CreateMedicationListDialog = (props: {
    onClose(medicationListCreated: boolean): void;
    onComplete(newId: string): void;
}) => {
    const [step, setStep] = useState(CreateMedicationListStep.ListInformation);
    const [errors, setErrors] = useState<ValidationFailure[]>([]);
    const [medicationListCreated, setMedicationListCreated] = useState(false);
    const throwError = useError();

    const medicationSettings = useMedicationSettings();

    const handleError = (errors: ValidationFailure[]) => {
        setStep(CreateMedicationListStep.Error);
        setErrors(errors);
    };

    const title = getTitle(step);
    const info = getFormInfo(step);
    const progress = getProgress(step);

    const handleClose = () => {
        props.onClose(medicationListCreated);
    };

    const handleBack = () => {
        if (step === CreateMedicationListStep.DefaultOverride) setStep(CreateMedicationListStep.ListSlices);
        else if (step === CreateMedicationListStep.ListSlices) setStep(CreateMedicationListStep.ListInformation);
    };

    const hideBackButton = step === CreateMedicationListStep.ListInformation || step === CreateMedicationListStep.Error;

    return (
        <FullScreenModal
            title={title}
            info={info}
            progressPercent={progress}
            onBack={handleBack}
            hideBackButton={hideBackButton}
            nonInteractivePlaceholder={
                step === CreateMedicationListStep.CreatingMedicationList ? (
                    <CreatingMedicationListPlaceholder />
                ) : undefined
            }
            onClose={handleClose}
        >
            <div className={styles.container}>
                <Formik<CreateMedicationListForm>
                    initialValues={{
                        name: '',
                        isScopeOfPracticeList: false,
                        isDefaultList: false,
                        isFormulary: false,
                        bulkAction: ListBulkAction.Append,
                        atcCodes: [],
                        schedules: [],
                        medicinalProducts: [],
                        defaultDuration: {
                            timeUnits: TimeUnits.Days,
                            number: ''
                        },
                        defaultDurationOverridden: false,
                        defaultDosageInstructions: '',
                        defaultDosageInstructionsOverridden: false,
                        defaultPackAmount: '',
                        defaultPackAmountOverridden: false
                    }}
                    validationSchema={createMedicationListSchema}
                    onSubmit={async (values, formikActions) => {
                        setStep(CreateMedicationListStep.CreatingMedicationList);
                        const timeout = new Promise(resolve => setTimeout(resolve, MIN_CREATE_DURATION));

                        const defaults = {
                            defaultDosageInstructions: values.defaultDosageInstructionsOverridden
                                ? values.defaultDosageInstructions
                                : '',
                            defaultDuration: values.defaultDurationOverridden
                                ? {
                                      timeUnits: values.defaultDuration.timeUnits,
                                      number: Number(values.defaultDuration.number)
                                  }
                                : null,
                            defaultPackAmount: values.defaultPackAmountOverridden
                                ? !!values.defaultPackAmount && !isNaN(Number(values.defaultPackAmount))
                                    ? Number(values.defaultPackAmount)
                                    : null
                                : null
                        };

                        const selectionSlicers: Slicer[] = [
                            ...values.schedules.map(
                                (it): Slicer => ({
                                    searchCode: it.code,
                                    searchAction: ListSearchAction.PoisonSchedule,
                                    ...defaults
                                })
                            ),
                            ...values.atcCodes.map(
                                (it): Slicer => ({
                                    searchCode: it.code,
                                    searchAction: ListSearchAction.AtcCode,
                                    ...defaults
                                })
                            ),
                            ...values.medicinalProducts.map(
                                (it): Slicer => ({
                                    searchCode: it.code,
                                    searchAction: ListSearchAction.MedicinalProduct,
                                    ...defaults
                                })
                            )
                        ];

                        const bulkActionPayload: BulkActionRequest = {
                            bulkAction: ListBulkAction.Append,
                            selectionSlicers
                        };

                        const createPayload: CreateMedicationList = {
                            authoringSystem: defaultAuthoringSystem,
                            authoringSystemForeignKey: selfAuthoringSystemForeignKey,
                            isDefaultList: values.isDefaultList,
                            isFormulary: values.isFormulary,
                            isScopeOfPracticeList: values.isScopeOfPracticeList,
                            name: values.name
                        };

                        try {
                            const result = await createToMedicationList(createPayload);
                            // This makes sure that when the dialog is closed,
                            // if a medication list was created but the bulk action failed, we still refresh the table.
                            setMedicationListCreated(true);
                            const [results] = await Promise.all([
                                applyBulkActionToMedicationList(result.id, bulkActionPayload),
                                timeout
                            ]);

                            const itemsAdded = sumBy(
                                results.results.filter(it => it.bulkAction === ListBulkAction.Append),
                                it => it.recordsChanged
                            );

                            if (itemsAdded) displayMessage(`Medication list created with ${itemsAdded} items.`);
                            else displayMessage(`Medication list created with no items.`);

                            formikActions.resetForm({ values });
                            props.onComplete(result.id);
                        } catch (err) {
                            // Error handling pattern:
                            // Capture all errors that we want to handle locally.
                            if (isDomainValidationError(err)) {
                                let errors: ValidationFailure[] = [];

                                if (err.content) {
                                    errors = [...err.content];
                                } else {
                                    errors = [
                                        {
                                            errorMessage: (err.message as string) ?? 'Unknown error occurred',
                                            propertyName: ''
                                        }
                                    ];
                                }

                                formikActions.resetForm({ values });
                                handleError(errors);
                            } else {
                                // Allow the rest to flow through to the 'throwError' so ErrorBoundary handles them.
                                throwError(err);
                            }
                        }
                    }}
                >
                    {(formProps: FormikProps<CreateMedicationListForm>) => {
                        const handleContinue = async () => {
                            const errors = await formProps.validateForm();

                            if (step === CreateMedicationListStep.ListInformation) {
                                const noErrors =
                                    Object.keys(errors).filter(it => createMedicationListStepFields.includes(it))
                                        .length === 0;
                                if (noErrors) setStep(CreateMedicationListStep.ListSlices);
                                else
                                    formProps.setTouched({
                                        name: true,
                                        isScopeOfPracticeList: true,
                                        isFormulary: true,
                                        isDefaultList: true,
                                        [MedicationListFormErrors_TypeMissing as keyof CreateMedicationListForm]: true
                                    });
                            } else if (step === CreateMedicationListStep.ListSlices) {
                                const noErrors =
                                    Object.keys(errors).filter(it =>
                                        ['atcCodes', 'schedules', 'medicinalProducts'].includes(it)
                                    ).length === 0;
                                if (noErrors) setStep(CreateMedicationListStep.DefaultOverride);
                                else
                                    formProps.setTouched({
                                        atcCodes: [{ code: true }],
                                        schedules: [{ code: true }],
                                        medicinalProducts: [{ code: true }]
                                    });
                            } else if (step === CreateMedicationListStep.DefaultOverride) {
                                formProps.submitForm();
                            }
                        };

                        return (
                            <>
                                {step === CreateMedicationListStep.ListInformation && <MedicationListInformationStep verbalOrderTerm={medicationSettings.settings.verbalOrderTerm} />}
                                {step === CreateMedicationListStep.ListSlices && (
                                    <MedicationListSliceStep onContinue={handleContinue} />
                                )}
                                {step === CreateMedicationListStep.DefaultOverride && <MedicationListDefaultOverride />}
                                {step === CreateMedicationListStep.Error && <MedicationListErrors errors={errors} />}
                                <CreateMedicationListButtons
                                    step={step}
                                    formProps={formProps}
                                    onClose={handleClose}
                                    onContinue={handleContinue}
                                />
                                <StyledNavigationPrompt when={formProps.isSubmitting || formProps.dirty} />
                            </>
                        );
                    }}
                </Formik>
            </div>
        </FullScreenModal>
    );
};
